import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  MenuItem,
  Grid2 as Grid,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Edit,
  Save,
  Close,
  Business,
  Payment,
  Store,
  EventNote,
  Lock,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalOffer as TariffIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { nowCv, todayCv } from '../../utils/date';

interface TarifarioEntity {
  ID: string;
  DESIG: string;
  CODIGO: string;
  ESTADO: string;
  SUBSCRICAO_ID: string | null;
  COMISSAO_PERCENTUAL: number | null;
  COMISSAO_FIXA: number | null;
  DATA_ASSINATURA: string | null;
  DATA_VALIDADE: string | null;
  SUB_ESTADO: string | null;
  PLANO_NOME: string | null;
  LOJA_NOME: string | null;
}

interface EventosPlanTarifa {
  id: number;
  plano: string;
  plano_label: string | null;
  is_default: number;
  preco_mensal: number;
  taxa_transacao_percentual: number;
  taxa_transacao_fixa: number;
  moeda: string;
  updated_at: string | null;
}

interface BillingTariff {
  id: string;
  code: string;
  name: string;
  description: string;
  tariffType: 'fixed' | 'percentage' | 'tiered' | 'custom';
  baseValue: number;
  percentageValue?: number;
  minValue?: number;
  maxValue?: number;
  status: 'active' | 'inactive';
  applicableFrom: string;
  applicableTo?: string;
  createdAt: string;
}

const PLANO_META: Record<string, { color: string; bg: string; label: string; description: string }> = {
  STARTER:    { color: '#64748b', bg: '#f1f5f9', label: 'Starter',    description: 'Eventos gratuitos — Free Pass apenas' },
  PRO:        { color: '#7c3aed', bg: '#ede9fe', label: 'Pro',        description: 'Bilhetes pagos, convites, chat, waitlist, hero slides' },
  ENTERPRISE: { color: '#b45309', bg: '#fef3c7', label: 'Enterprise', description: 'Tudo do Pro + NFC, cashless, gamificação' },
};

const Tarifario: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // ---------- tab 0: por entidade ----------
  const [entities, setEntities] = useState<TarifarioEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [editEntity, setEditEntity] = useState<TarifarioEntity | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [comissaoPercentual, setComissaoPercentual] = useState<string>('');
  const [comissaoFixa, setComissaoFixa] = useState<string>('');

  // ---------- tab 1: planos de eventos ----------
  const [eventPlans, setEventPlans] = useState<EventosPlanTarifa[]>([]);
  const [eventPlansLoading, setEventPlansLoading] = useState(false);
  const [editPlan, setEditPlan] = useState<EventosPlanTarifa | null>(null);
  const [planEditOpen, setPlanEditOpen] = useState(false);
  const [planPrecoMensal, setPlanPrecoMensal] = useState('');
  const [planTaxaPercentual, setPlanTaxaPercentual] = useState('');
  const [planTaxaFixa, setPlanTaxaFixa] = useState('');
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [newPlanForm, setNewPlanForm] = useState({ plano: '', planoLabel: '', precoMensal: '', taxaPercentual: '', taxaFixa: '' });
  const [newPlanSaving, setNewPlanSaving] = useState(false);
  const [deleteEntityOpen, setDeleteEntityOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<TarifarioEntity | null>(null);

  // ---------- tab 2: por serviços (billing tariffs) ----------
  const [billingTariffs, setBillingTariffs] = useState<BillingTariff[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingSearch, setBillingSearch] = useState('');
  const [billingTypeFilter, setBillingTypeFilter] = useState('all');
  const [billingStatusFilter, setBillingStatusFilter] = useState('all');
  const [billingPage, setBillingPage] = useState(0);
  const [billingRowsPerPage, setBillingRowsPerPage] = useState(10);
  const [billingEditDialog, setBillingEditDialog] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<BillingTariff | null>(null);
  const [tariffForm, setTariffForm] = useState({
    code: '', name: '', description: '',
    tariffType: 'fixed' as 'fixed' | 'percentage' | 'tiered' | 'custom',
    baseValue: 0, percentageValue: 0, minValue: 0, maxValue: 0,
    status: 'active' as 'active' | 'inactive',
    applicableFrom: todayCv(),
    applicableTo: '',
  });

  // ---------- shared ----------
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ---------- loaders ----------
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/shop/tarifario', {
        params: { search, page: page + 1, limit: rowsPerPage },
      });
      if (response.data.success) {
        setEntities(response.data.data);
        setTotal(response.data.pagination.total);
      }
    } catch (error: any) {
      console.error('Error loading tarifario:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar tarifário', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, page, rowsPerPage]);

  const loadEventPlans = useCallback(async () => {
    try {
      setEventPlansLoading(true);
      const response = await api.get('/admin/shop/tarifario-eventos');
      if (response.data.success) setEventPlans(response.data.data);
    } catch (error: any) {
      console.error('Error loading event plans:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar planos de eventos', severity: 'error' });
    } finally {
      setEventPlansLoading(false);
    }
  }, []);

  const loadBillingTariffs = useCallback(async () => {
    setBillingLoading(true);
    setBillingError(null);
    try {
      const response = await api.get('/admin/invoices/tariffs');
      if (response.data.success) setBillingTariffs(response.data.data || []);
    } catch (err: any) {
      setBillingError('Erro ao carregar tarifários de serviços');
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 1) loadEventPlans(); }, [activeTab, loadEventPlans]);
  useEffect(() => { if (activeTab === 2) loadBillingTariffs(); }, [activeTab, loadBillingTariffs]);

  // ---------- entity handlers ----------
  const handleEdit = (entity: TarifarioEntity) => {
    setEditEntity(entity);
    setComissaoPercentual(entity.COMISSAO_PERCENTUAL?.toString() || '3.50');
    setComissaoFixa(entity.COMISSAO_FIXA?.toString() || '0');
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editEntity) return;
    try {
      const response = await api.put(`/admin/shop/tarifario/${editEntity.ID}`, {
        comissaoPercentual: parseFloat(comissaoPercentual) || 0,
        comissaoFixa: parseFloat(comissaoFixa) || 0,
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Tarifário atualizado com sucesso', severity: 'success' });
        setEditOpen(false);
        loadData();
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar', severity: 'error' });
    }
  };

  const handleDeleteEntity = async () => {
    if (!entityToDelete) return;
    try {
      await api.delete(`/admin/shop/tarifario/${entityToDelete.ID}`);
      setSnackbar({ open: true, message: 'Comissão removida', severity: 'success' });
      setDeleteEntityOpen(false);
      setEntityToDelete(null);
      loadData();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao remover', severity: 'error' });
    }
  };

  // ---------- plan handlers ----------
  const handleEditPlan = (plan: EventosPlanTarifa) => {
    setEditPlan(plan);
    setPlanPrecoMensal(plan.preco_mensal.toString());
    setPlanTaxaPercentual(plan.taxa_transacao_percentual.toString());
    setPlanTaxaFixa(plan.taxa_transacao_fixa.toString());
    setPlanEditOpen(true);
  };

  const handleSavePlan = async () => {
    if (!editPlan) return;
    try {
      const response = await api.put(`/admin/shop/tarifario-eventos/${editPlan.plano}`, {
        precoMensal: parseFloat(planPrecoMensal) || 0,
        taxaTransacaoPercentual: parseFloat(planTaxaPercentual) || 0,
        taxaTransacaoFixa: parseFloat(planTaxaFixa) || 0,
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Tarifa do plano atualizada', severity: 'success' });
        setPlanEditOpen(false);
        loadEventPlans();
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar plano', severity: 'error' });
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlanForm.plano || !newPlanForm.planoLabel) return;
    setNewPlanSaving(true);
    try {
      await api.post('/admin/shop/tarifario-eventos', {
        plano: newPlanForm.plano,
        planoLabel: newPlanForm.planoLabel,
        precoMensal: parseFloat(newPlanForm.precoMensal) || 0,
        taxaTransacaoPercentual: parseFloat(newPlanForm.taxaPercentual) || 0,
        taxaTransacaoFixa: parseFloat(newPlanForm.taxaFixa) || 0,
      });
      setSnackbar({ open: true, message: 'Plano criado com sucesso', severity: 'success' });
      setNewPlanOpen(false);
      setNewPlanForm({ plano: '', planoLabel: '', precoMensal: '', taxaPercentual: '', taxaFixa: '' });
      loadEventPlans();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao criar plano', severity: 'error' });
    } finally {
      setNewPlanSaving(false);
    }
  };

  const handleDeletePlan = async (plan: EventosPlanTarifa) => {
    if (!window.confirm(`Eliminar o plano "${plan.plano_label || plan.plano}"?`)) return;
    try {
      await api.delete(`/admin/shop/tarifario-eventos/${plan.plano}`);
      setSnackbar({ open: true, message: 'Plano eliminado', severity: 'success' });
      loadEventPlans();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao eliminar plano', severity: 'error' });
    }
  };

  // ---------- billing tariff handlers ----------
  const billingTypeChip = (type: string) => {
    const cfg: Record<string, { color: 'primary' | 'secondary' | 'info' | 'warning'; label: string }> = {
      fixed: { color: 'primary', label: 'Fixo' },
      percentage: { color: 'secondary', label: 'Percentual' },
      tiered: { color: 'info', label: 'Escalonado' },
      custom: { color: 'warning', label: 'Personalizado' },
    };
    const { color, label } = cfg[type] || cfg.fixed;
    return <Chip size="small" color={color} label={label} />;
  };

  const billingStatusChip = (status: string) =>
    status === 'active'
      ? <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />
      : <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />;

  const formatCurrency = (value: number) => `${Math.round(value / 100).toLocaleString('pt-PT')}$`;

  const getTariffValue = (t: BillingTariff) => {
    if (t.tariffType === 'percentage') return `${t.percentageValue}%`;
    if (t.tariffType === 'fixed') return formatCurrency(t.baseValue);
    if (t.tariffType === 'tiered') return `${formatCurrency(t.minValue || 0)} - ${formatCurrency(t.maxValue || 0)}`;
    return 'Personalizado';
  };

  const handleOpenBillingEdit = (tariff?: BillingTariff) => {
    if (tariff) {
      setSelectedTariff(tariff);
      setTariffForm({
        code: tariff.code, name: tariff.name, description: tariff.description,
        tariffType: tariff.tariffType, baseValue: tariff.baseValue,
        percentageValue: tariff.percentageValue || 0, minValue: tariff.minValue || 0,
        maxValue: tariff.maxValue || 0, status: tariff.status,
        applicableFrom: tariff.applicableFrom, applicableTo: tariff.applicableTo || '',
      });
    } else {
      setSelectedTariff(null);
      setTariffForm({
        code: '', name: '', description: '', tariffType: 'fixed',
        baseValue: 0, percentageValue: 0, minValue: 0, maxValue: 0,
        status: 'active', applicableFrom: todayCv(), applicableTo: '',
      });
    }
    setBillingEditDialog(true);
  };

  const handleSaveBillingTariff = async () => {
    setBillingSaving(true);
    try {
      const payload = {
        ...tariffForm,
        percentageValue: tariffForm.tariffType === 'percentage' ? tariffForm.percentageValue : undefined,
        minValue: tariffForm.minValue > 0 ? tariffForm.minValue : undefined,
        maxValue: tariffForm.maxValue > 0 ? tariffForm.maxValue : undefined,
        applicableTo: tariffForm.applicableTo || undefined,
      };
      if (selectedTariff) {
        await api.put(`/admin/invoices/tariffs/${selectedTariff.id}`, payload);
      } else {
        await api.post('/admin/invoices/tariffs', payload);
      }
      setBillingEditDialog(false);
      loadBillingTariffs();
      setSnackbar({ open: true, message: 'Tarifário de serviços guardado', severity: 'success' });
    } catch (err: any) {
      setBillingError('Erro ao guardar tarifário');
    } finally {
      setBillingSaving(false);
    }
  };

  const handleDeleteBillingTariff = async (tariff: BillingTariff) => {
    if (!window.confirm(`Eliminar tarifário "${tariff.name}"?`)) return;
    try {
      await api.delete(`/admin/invoices/tariffs/${tariff.id}`);
      loadBillingTariffs();
    } catch {
      setBillingError('Erro ao eliminar tarifário');
    }
  };

  const filteredBillingTariffs = billingTariffs.filter((t) => {
    const matchSearch = billingSearch === '' ||
      t.name.toLowerCase().includes(billingSearch.toLowerCase()) ||
      t.code.toLowerCase().includes(billingSearch.toLowerCase());
    const matchType = billingTypeFilter === 'all' || t.tariffType === billingTypeFilter;
    const matchStatus = billingStatusFilter === 'all' || t.status === billingStatusFilter;
    return matchSearch && matchType && matchStatus;
  });

  // ---------- Stats ----------
  const comConfigurada = entities.filter(e => e.COMISSAO_PERCENTUAL != null).length;
  const semConfigurada = entities.filter(e => e.COMISSAO_PERCENTUAL == null).length;
  const comLoja = entities.filter(e => e.LOJA_NOME != null).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Tarifário</Typography>
          <Typography variant="body2" color="text.secondary">
            Comissões por entidade e tarifas dos planos de eventos
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Por Entidade" icon={<Business />} iconPosition="start" />
        <Tab label="Planos de Eventos" icon={<EventNote />} iconPosition="start" />
        <Tab label="Por Serviços" icon={<TariffIcon />} iconPosition="start" />
      </Tabs>

      {/* ===== TAB 0: Por Entidade ===== */}
      {activeTab === 0 && (
        <>
          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Business color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Entidades</Typography>
                  <Typography variant="h5" fontWeight="bold">{total}</Typography>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Payment color="success" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Com Comissao Configurada</Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{comConfigurada}</Typography>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Store color="info" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Com Loja Ativa</Typography>
                  <Typography variant="h5" fontWeight="bold" color="info.main">{comLoja}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Search */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar entidade..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search /></InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>

          {/* Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Entidade</strong></TableCell>
                  <TableCell><strong>Codigo</strong></TableCell>
                  <TableCell><strong>Loja</strong></TableCell>
                  <TableCell><strong>Plano</strong></TableCell>
                  <TableCell align="center"><strong>Comissao %</strong></TableCell>
                  <TableCell align="center"><strong>Comissao Fixa (CVE)</strong></TableCell>
                  <TableCell align="center"><strong>Validade</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acoes</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>A carregar...</TableCell>
                  </TableRow>
                ) : entities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>Nenhuma entidade encontrada</TableCell>
                  </TableRow>
                ) : (
                  entities.map((entity) => (
                    <TableRow key={entity.ID} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{entity.DESIG}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{entity.CODIGO}</Typography>
                      </TableCell>
                      <TableCell>
                        {entity.LOJA_NOME ? (
                          <Chip label={entity.LOJA_NOME} size="small" color="info" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {entity.PLANO_NOME ? (
                          <Chip label={entity.PLANO_NOME} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entity.COMISSAO_PERCENTUAL != null ? (
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {entity.COMISSAO_PERCENTUAL}%
                          </Typography>
                        ) : (
                          <Chip label="N/D" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entity.COMISSAO_FIXA != null && parseFloat(String(entity.COMISSAO_FIXA)) > 0 ? (
                          <Typography variant="body2" fontWeight="bold">{entity.COMISSAO_FIXA} CVE</Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entity.DATA_VALIDADE ? (
                          <Typography variant="body2" color={
                            new Date(entity.DATA_VALIDADE) < nowCv() ? 'error.main' : 'text.secondary'
                          }>
                            {new Date(entity.DATA_VALIDADE).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entity.SUB_ESTADO === 'A' ? (
                          <Chip label="Ativa" size="small" color="success" />
                        ) : entity.SUBSCRICAO_ID ? (
                          <Chip label="Inativa" size="small" color="default" />
                        ) : (
                          <Chip label="Sem Sub." size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(entity)} title="Editar comissão">
                          <Edit fontSize="small" />
                        </IconButton>
                        {entity.SUBSCRICAO_ID && (
                          <IconButton size="small" color="error" title="Remover comissão"
                            onClick={() => { setEntityToDelete(entity); setDeleteEntityOpen(true); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Linhas por pagina"
            />
          </TableContainer>
        </>
      )}

      {/* ===== TAB 1: Planos de Eventos ===== */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
              Tarifas cobradas aos promotores na reconciliação mensal. O plano <strong>Starter</strong> é sempre gratuito.
            </Alert>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewPlanOpen(true)}>
              Novo Plano
            </Button>
          </Box>

          {eventPlansLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {eventPlans.map((plan) => {
                const meta = PLANO_META[plan.plano] ?? { color: '#1976d2', bg: '#e3f2fd', label: plan.plano_label || plan.plano, description: 'Plano personalizado' };
                const isStarter = plan.plano === 'STARTER';
                const isDefault = plan.is_default === 1;
                return (
                  <Card key={plan.plano} sx={{ flex: 1, minWidth: 260, borderTop: `4px solid ${meta.color}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Chip
                            label={plan.plano_label || plan.plano}
                            size="small"
                            sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, mb: 0.5 }}
                          />
                          {isDefault && (
                            <Chip label="Padrão" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {meta.description}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {isStarter ? (
                            <Lock sx={{ color: 'text.disabled', fontSize: 20, mt: 1 }} />
                          ) : (
                            <>
                              <IconButton size="small" color="primary" onClick={() => handleEditPlan(plan)} title="Editar">
                                <Edit fontSize="small" />
                              </IconButton>
                              {!isDefault && (
                                <IconButton size="small" color="error" onClick={() => handleDeletePlan(plan)} title="Eliminar">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Subscrição mensal</Typography>
                          <Typography variant="h6" fontWeight="bold" color={isStarter ? 'success.main' : meta.color}>
                            {isStarter ? 'Grátis' : `${Number(plan.preco_mensal).toLocaleString('pt-CV')} CVE`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Taxa por transação</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {isStarter ? '0%' : `${plan.taxa_transacao_percentual}%`}
                            {!isStarter && Number(plan.taxa_transacao_fixa) > 0 && ` + ${plan.taxa_transacao_fixa} CVE`}
                          </Typography>
                        </Box>
                      </Box>

                      {plan.updated_at && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                          Atualizado em {new Date(plan.updated_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ===== TAB 2: Por Serviços ===== */}
      {activeTab === 2 && (
        <Box>
          {billingError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillingError(null)}>{billingError}</Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenBillingEdit()}>
              Novo Tarifário
            </Button>
          </Box>

          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <TariffIcon color="primary" />, label: 'Total', value: billingTariffs.length },
              { icon: <ActiveIcon color="success" />, label: 'Activos', value: billingTariffs.filter(t => t.status === 'active').length },
              { icon: <TariffIcon color="info" />, label: 'Taxa Fixa', value: billingTariffs.filter(t => t.tariffType === 'fixed').length },
              { icon: <TariffIcon color="secondary" />, label: 'Taxa Percentual', value: billingTariffs.filter(t => t.tariffType === 'percentage').length },
            ].map((s, i) => (
              <Grid key={i} size={{ xs: 6, md: 3 }}>
                <Card>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {s.icon}
                      <Box>
                        <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth size="small" placeholder="Pesquisar tarifários..."
                    value={billingSearch} onChange={(e) => setBillingSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth size="small" select label="Tipo" value={billingTypeFilter} onChange={(e) => setBillingTypeFilter(e.target.value)}>
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="fixed">Fixo</MenuItem>
                    <MenuItem value="percentage">Percentual</MenuItem>
                    <MenuItem value="tiered">Escalonado</MenuItem>
                    <MenuItem value="custom">Personalizado</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth size="small" select label="Estado" value={billingStatusFilter} onChange={(e) => setBillingStatusFilter(e.target.value)}>
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="active">Activos</MenuItem>
                    <MenuItem value="inactive">Inactivos</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Table */}
          {billingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={100}>Código</TableCell>
                      <TableCell>Nome</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell width={130}>Tipo</TableCell>
                      <TableCell width={150}>Valor</TableCell>
                      <TableCell width={110}>Válido Desde</TableCell>
                      <TableCell width={100}>Estado</TableCell>
                      <TableCell width={100} align="center">Acções</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBillingTariffs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum tarifário encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredBillingTariffs
                        .slice(billingPage * billingRowsPerPage, billingPage * billingRowsPerPage + billingRowsPerPage)
                        .map((t) => (
                          <TableRow key={t.id} hover>
                            <TableCell><Typography variant="body2" fontWeight={600} fontFamily="monospace">{t.code}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontWeight={500}>{t.name}</Typography></TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>{t.description}</Typography></TableCell>
                            <TableCell>{billingTypeChip(t.tariffType)}</TableCell>
                            <TableCell><Typography variant="body2" fontWeight={500}>{getTariffValue(t)}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontSize="0.75rem">{t.applicableFrom}</Typography></TableCell>
                            <TableCell>{billingStatusChip(t.status)}</TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => handleOpenBillingEdit(t)}><Edit fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteBillingTariff(t)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredBillingTariffs.length}
                page={billingPage}
                onPageChange={(_, p) => setBillingPage(p)}
                rowsPerPage={billingRowsPerPage}
                onRowsPerPageChange={(e) => { setBillingRowsPerPage(parseInt(e.target.value)); setBillingPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* ===== Billing Tariff Edit Dialog ===== */}
      <Dialog open={billingEditDialog} onClose={() => setBillingEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TariffIcon />
          {selectedTariff ? 'Editar Tarifário' : 'Novo Tarifário'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Código" value={tariffForm.code}
                onChange={(e) => setTariffForm({ ...tariffForm, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }} disabled={!!selectedTariff} />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField fullWidth label="Nome" value={tariffForm.name}
                onChange={(e) => setTariffForm({ ...tariffForm, name: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Descrição" value={tariffForm.description}
                onChange={(e) => setTariffForm({ ...tariffForm, description: e.target.value })}
                multiline rows={2} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth select label="Tipo de Tarifário" value={tariffForm.tariffType}
                onChange={(e) => setTariffForm({ ...tariffForm, tariffType: e.target.value as any })}>
                <MenuItem value="fixed">Taxa Fixa</MenuItem>
                <MenuItem value="percentage">Taxa Percentual</MenuItem>
                <MenuItem value="tiered">Taxa Escalonada</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth select label="Estado" value={tariffForm.status}
                onChange={(e) => setTariffForm({ ...tariffForm, status: e.target.value as any })}>
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
            {tariffForm.tariffType === 'fixed' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth type="number" label="Valor Base (cêntimos)" value={tariffForm.baseValue}
                  onChange={(e) => setTariffForm({ ...tariffForm, baseValue: parseInt(e.target.value) || 0 })}
                  helperText={`${Math.round(tariffForm.baseValue / 100).toLocaleString('pt-PT')}$`} />
              </Grid>
            )}
            {tariffForm.tariffType === 'percentage' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth type="number" label="Percentagem (%)" value={tariffForm.percentageValue}
                    onChange={(e) => setTariffForm({ ...tariffForm, percentageValue: parseFloat(e.target.value) || 0 })}
                    inputProps={{ step: 0.1, min: 0, max: 100 }} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth type="number" label="Valor Mínimo (cêntimos)" value={tariffForm.minValue}
                    onChange={(e) => setTariffForm({ ...tariffForm, minValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(tariffForm.minValue / 100).toLocaleString('pt-PT')}$`} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth type="number" label="Valor Máximo (cêntimos)" value={tariffForm.maxValue}
                    onChange={(e) => setTariffForm({ ...tariffForm, maxValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(tariffForm.maxValue / 100).toLocaleString('pt-PT')}$`} />
                </Grid>
              </>
            )}
            {tariffForm.tariffType === 'tiered' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth type="number" label="Valor Base (cêntimos)" value={tariffForm.baseValue}
                    onChange={(e) => setTariffForm({ ...tariffForm, baseValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(tariffForm.baseValue / 100).toLocaleString('pt-PT')}$`} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth type="number" label="Valor Mínimo (cêntimos)" value={tariffForm.minValue}
                    onChange={(e) => setTariffForm({ ...tariffForm, minValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(tariffForm.minValue / 100).toLocaleString('pt-PT')}$`} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth type="number" label="Valor Máximo (cêntimos)" value={tariffForm.maxValue}
                    onChange={(e) => setTariffForm({ ...tariffForm, maxValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(tariffForm.maxValue / 100).toLocaleString('pt-PT')}$`} />
                </Grid>
              </>
            )}
            {tariffForm.tariffType === 'custom' && (
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth type="number" label="Valor Referência (cêntimos)" value={tariffForm.baseValue}
                  onChange={(e) => setTariffForm({ ...tariffForm, baseValue: parseInt(e.target.value) || 0 })}
                  helperText={`${Math.round(tariffForm.baseValue / 100).toLocaleString('pt-PT')}$ — Valor de referência para cálculo personalizado`} />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth type="date" label="Aplicável Desde" value={tariffForm.applicableFrom}
                onChange={(e) => setTariffForm({ ...tariffForm, applicableFrom: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth type="date" label="Aplicável Até (opcional)" value={tariffForm.applicableTo}
                onChange={(e) => setTariffForm({ ...tariffForm, applicableTo: e.target.value })}
                InputLabelProps={{ shrink: true }} helperText="Deixe vazio para aplicação indefinida" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveBillingTariff}
            disabled={billingSaving || !tariffForm.code || !tariffForm.name || !tariffForm.applicableFrom}>
            {billingSaving ? <CircularProgress size={20} /> : selectedTariff ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Edit Entity Dialog ===== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Editar Tarifario - {editEntity?.DESIG}
          <IconButton onClick={() => setEditOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            A comissao Pagali e cobrada sobre cada transacao. Inclui a taxa SISP (Vinti4: 0.70%, Visa: 2.50%) + margem Pagali.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Entidade: <strong>{editEntity?.DESIG}</strong> ({editEntity?.CODIGO})
              </Typography>
              {editEntity?.LOJA_NOME && (
                <Typography variant="subtitle2" color="text.secondary">
                  Loja: <strong>{editEntity.LOJA_NOME}</strong>
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Comissao Percentual (%)"
                type="number"
                value={comissaoPercentual}
                onChange={(e) => setComissaoPercentual(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText="Ex: 3.50 = 3.50% por transacao"
              />
              <TextField
                fullWidth
                label="Comissao Fixa (CVE)"
                type="number"
                value={comissaoFixa}
                onChange={(e) => setComissaoFixa(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Valor fixo por transacao (opcional)"
              />
            </Box>
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2">
                <strong>Exemplo:</strong> Numa venda de 10.000 CVE com {comissaoPercentual}% + {comissaoFixa} CVE fixo:
              </Typography>
              <Typography variant="body2">
                Comissao = {((10000 * parseFloat(comissaoPercentual || '0')) / 100 + parseFloat(comissaoFixa || '0')).toFixed(0)} CVE |
                Entidade recebe = {(10000 - (10000 * parseFloat(comissaoPercentual || '0')) / 100 - parseFloat(comissaoFixa || '0')).toFixed(0)} CVE
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} startIcon={<Close />}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<Save />}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* ===== Edit Plan Dialog ===== */}
      <Dialog open={planEditOpen} onClose={() => setPlanEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Editar Tarifa — {editPlan ? (editPlan.plano_label || editPlan.plano) : ''}
          <IconButton onClick={() => setPlanEditOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            Valores cobrados ao promotor mensalmente via reconciliação de vendas.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Subscrição Mensal (CVE)"
              type="number"
              value={planPrecoMensal}
              onChange={(e) => setPlanPrecoMensal(e.target.value)}
              inputProps={{ min: 0, step: 100 }}
              helperText="Valor fixo mensal independente do volume de vendas"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Taxa por Transação (%)"
                type="number"
                value={planTaxaPercentual}
                onChange={(e) => setPlanTaxaPercentual(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText="Ex: 3.50 = 3.50% por bilhete vendido"
              />
              <TextField
                fullWidth
                label="Taxa Fixa por Transação (CVE)"
                type="number"
                value={planTaxaFixa}
                onChange={(e) => setPlanTaxaFixa(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Valor fixo adicional por bilhete (opcional)"
              />
            </Box>
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2">
                <strong>Exemplo:</strong> Evento com 100 bilhetes a 2.000 CVE (total 200.000 CVE):
              </Typography>
              <Typography variant="body2">
                Subscrição = {Number(planPrecoMensal || 0).toLocaleString()} CVE |{' '}
                Taxa vendas = {((200000 * parseFloat(planTaxaPercentual || '0')) / 100 + parseFloat(planTaxaFixa || '0') * 100).toFixed(0)} CVE
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanEditOpen(false)} startIcon={<Close />}>Cancelar</Button>
          <Button onClick={handleSavePlan} variant="contained" startIcon={<Save />}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* ===== Confirm Delete Entity Dialog ===== */}
      <Dialog open={deleteEntityOpen} onClose={() => setDeleteEntityOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Comissão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem a certeza que quer remover a comissão de <strong>{entityToDelete?.DESIG}</strong>?
            A subscrição será desativada.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEntityOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteEntity} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== New Plan Dialog ===== */}
      <Dialog open={newPlanOpen} onClose={() => setNewPlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Novo Plano de Eventos
          <IconButton onClick={() => setNewPlanOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth label="Código do plano" placeholder="Ex: PREMIUM"
                value={newPlanForm.plano}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, plano: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                helperText="Código interno único, sem espaços"
                inputProps={{ maxLength: 30 }}
              />
              <TextField
                fullWidth label="Nome do plano" placeholder="Ex: Premium"
                value={newPlanForm.planoLabel}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, planoLabel: e.target.value })}
                helperText="Nome visível ao utilizador"
              />
            </Box>
            <TextField
              fullWidth label="Subscrição Mensal (CVE)" type="number"
              value={newPlanForm.precoMensal}
              onChange={(e) => setNewPlanForm({ ...newPlanForm, precoMensal: e.target.value })}
              inputProps={{ min: 0, step: 100 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth label="Taxa por Transação (%)" type="number"
                value={newPlanForm.taxaPercentual}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, taxaPercentual: e.target.value })}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
              />
              <TextField
                fullWidth label="Taxa Fixa por Transação (CVE)" type="number"
                value={newPlanForm.taxaFixa}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, taxaFixa: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewPlanOpen(false)} startIcon={<Close />}>Cancelar</Button>
          <Button
            onClick={handleCreatePlan}
            variant="contained"
            startIcon={newPlanSaving ? <CircularProgress size={16} /> : <Save />}
            disabled={newPlanSaving || !newPlanForm.plano || !newPlanForm.planoLabel}
          >
            Criar Plano
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tarifario;
